import { Box, Button, Chip, Grid, Typography, makeStyles } from "@material-ui/core";
import { KeyboardDatePicker } from "@material-ui/pickers";
import React, { useEffect, useState } from "react";
import { getInputDateFormats } from "../../../../utils/get-date-time-locale";
import { Select } from "../../../Common/Select";

import { Close } from "@material-ui/icons";
import { startOfDay } from "date-fns";
import { useTranslation } from "react-i18next";
import { useFormCombos } from "../../../../hooks/useFormCombos";
import { getCommittees } from "../../../../services/committees.service";
import { getCompaniesByUser } from "../../../../services/companies.service";
import { getMembersByName } from "../../../../services/formCombos.service.js";
import Textarea from "../../../Common/Textarea";
import {
  ButtonsContainer,
  Content,
  FieldsContainer,
  HeaderContainer,
  OpenedInputDescription,
} from "./styles";

const useStyles = makeStyles((theme) => ({
  button: {
    fontSize: "1rem",
    fontWeight: "bold",
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.primary.contrastText,
    textTransform: "capitalize",
    minWidth: 180,
    minHeight: 50,
  },
  buttonOutlined: {
    border: "2px solid",
    fontSize: "1rem",
    fontWeight: "bold",
    color: theme.palette.grey.main,
    textTransform: "capitalize",
    minWidth: 180,
    minHeight: 50,
  },
  chip: {
    margin: 2,
  },
}));

function FilterDrawer({
  onClose,
  t,
  filters,
  setFilters,
  actionShowSpinner,
  canListDeleteFiles,
  cleanFilters,
}) {
  const classes = useStyles();
  const typesOptions = [
    { id: 1, name: t("fileTypes.action") },
    { id: 2, name: t("fileTypes.schedule") },
    { id: 3, name: t("fileTypes.minutes") },
    { id: 4, name: t("fileTypes.statementOfDeliberation") },
    { id: 5, name: t("fileTypes.forms") },
    { id: 6, name: t("fileTypes.supportMaterial") },
  ];
  const { i18n } = useTranslation();

  const [committeesOptions, setCommitteesOptions] = useState([]);
  const [committee, setCommittee] = useState(filters.committee);
  const [meetStartDate, setMeetStartDate] = useState(filters.startDate);
  const [meetEndDate, setMeetEndDate] = useState(filters.endDate);

  const [presenterInputValue, setPresenterInputValue] = useState("");
  const [presenters, setPresenters] = useState([]);
  const [selectedPresenter, setSelectedPresenter] = useState(
    filters.selectedPresenter
  );
  const [requesterInputValue, setRequesterInputValue] = useState("");
  const [requesters, setRequesters] = useState([]);
  const [selectedRequester, setSelectedRequester] = useState(
    filters.requesterId
  );
  const [directory, setDirectory] = useState(filters.directorship);
  const [fileType, setFileType] = useState(filters.fileType);
  const [openedField, setOpenedField] = useState(filters.keyword);
  const [selectedCompanies, setSelectedCompanies] = useState(filters.companies)
  const [companiesOptions, setCompaniesOptions] = useState([])

  const { getDirectors, directorsList, sanitizeDirectorsForSelect } =
    useFormCombos();

  useEffect(() => {
    const loadCommitteeOptions = async () => {
      const response = await getCommittees({
        showDisabled: true,
        pageSize: 999999
      });
      
      setCommitteesOptions(
        response.data.items
      );
    };
    const loadCompaniesOptions = async () => {
      const companies = await getCompaniesByUser();
      setCompaniesOptions(companies);
    };

    actionShowSpinner(true);

    loadCommitteeOptions();
    loadCompaniesOptions();

    getDirectors();

    actionShowSpinner(false);
  }, []);

  useEffect(() => {
    setSelectedCompanies(filters?.companies)
    setSelectedPresenter(filters?.presenter);
    setSelectedRequester(filters?.requesterId);
    setDirectory(filters?.directorship);
    setFileType(filters?.fileType);
    setOpenedField(filters?.keyword);
    setCommittee(filters?.committee);
    setMeetStartDate(filters?.startDate);
    setMeetEndDate(filters?.endDate);
  }, [filters]);

  useEffect(() => {
    async function load() {
      if (requesterInputValue?.length >= 3) {
        const res = await getMembersByName("pt", requesterInputValue);

        if (res.error?.length > 0) {
          setRequesters([]);
        }

        setRequesters(res.data);
      } else {
        setRequesters([]);
      }

      if (presenterInputValue?.length >= 3) {
        const res = await getMembersByName("pt", presenterInputValue);

        if (res.error?.length > 0) {
          setPresenters([]);
        }

        setPresenters(res.data);
      } else {
        setPresenters([]);
      }
    }

    load();
  }, [requesterInputValue, presenterInputValue]);

  const handleApplyFilters = () => {
    setFilters({
      ...(selectedPresenter && { presenter: selectedPresenter }),
      ...(selectedCompanies && { companies: selectedCompanies }),
      ...(selectedRequester && { requesterId: selectedRequester }),
      ...(directory && { directorship: directory }),
      ...(fileType && { fileType: fileType }),
      ...(openedField && { keyword: openedField }),
      ...(committee && { committee: committee }),
      ...(meetStartDate ? { startDate: meetStartDate } : { startDate: null }),
      ...(meetEndDate ? { endDate: meetEndDate } : { endDate: null }),
    });

    onClose();
  };

  const handleCleanFilters = () => {
    cleanFilters();

    onClose();
  };

  return (
    <Box sx={{ width: 460, flex: 1, display: "flex" }} role="presentation">
      <Content>
        <HeaderContainer>
          <Typography variant="h4">{t("forms.filters")}</Typography>

          <Close
            onClick={() => {
              onClose();
            }}
          />
        </HeaderContainer>

        <FieldsContainer>
          <Grid container spacing={3}>
            <Grid item lg={12} xs={12}>
              <Select
                fullWidth
                id="requester"
                options={requesters}
                value={selectedRequester}
                onInputChange={(_event, newInputValue, _prop) => {
                  setRequesterInputValue(newInputValue);
                }}
                inputValue={requesterInputValue}
                label={t("forms.minutes.actions.requesterName")}
                setValue={(value) => {
                  setSelectedRequester(value);
                }}
              />
            </Grid>

            <Grid item lg={12} xs={12}>
              <Select
                fullWidth
                id="presenter"
                options={presenters}
                value={selectedPresenter}
                onInputChange={(_event, newInputValue, _prop) => {
                  setPresenterInputValue(newInputValue);
                }}
                inputValue={presenterInputValue}
                label={t("slots.presenter")}
                setValue={(value) => {
                  setSelectedPresenter(value);
                }}
              />
            </Grid>

            <Grid item lg={12} xs={12}>
              <Select
                fullWidth
                id="directories"
                options={sanitizeDirectorsForSelect(directorsList)}
                value={directory}
                label={t("slots.board")}
                setValue={(value) => {
                  setDirectory(value);
                }}
              />
            </Grid>

            <Grid item lg={12} xs={12}>
              <Select
                id="fileType"
                options={typesOptions}
                label={t("typeFile")}
                value={fileType}
                setValue={(value) => {
                  setFileType(value);
                }}
              />
            </Grid>
            <Grid item lg={12} xs={12}>
              <Select
                id="committee"
                options={committeesOptions}
                value={committee}
                label={t("slots.committee")}
                setValue={(value) => {
                  setCommittee(value);
                }}
              />
            </Grid>

            <Grid item lg={12} xs={12}>
              <Select
                id="companies"
                options={
                  companiesOptions?.map((item) => ({
                    id: item.companyId,
                    name: `${item.companyName} - ${item.companyAbbreviation}`,
                  })) ?? []
                }
                getOptionSelected={(option, value) => option.id === value.id}
                value={selectedCompanies}
                label={t("menu.fileManagement.info.companies")}
                setValue={(values) => {
                  setSelectedCompanies(values);
                }}
                multiple={true}
                renderTags={(selectedValues) => {
                  return selectedValues.map((option) => (
                    <Chip
                      className={classes.chip}
                      key={option.id}
                      onDelete={() => {
                        setSelectedCompanies(
                          selectedCompanies.filter((v) => v.id !== option.id)
                        );
                      }}
                      onClick={() => {
                        setSelectedCompanies(
                          selectedCompanies.filter((v) => v.id !== option.id)
                        );
                      }}
                      variant="outlined"
                      label={option.name}
                    />
                  ));
                }}
              />
            </Grid>

            <Grid item lg={6} xs={6}>
              <KeyboardDatePicker
                autoOk
                disableToolbar
                variant="inline"
                format={getInputDateFormats(i18n.language)}
                id="createdIn"
                fullWidth
                label={t("forms.startDateLabel")}
                value={meetStartDate}
                onChange={(date) =>
                  setMeetStartDate(date ? startOfDay(date) : null)
                }
                KeyboardButtonProps={{
                  "aria-label": "change date",
                }}
              />
            </Grid>

            <Grid item lg={6} xs={6}>
              <KeyboardDatePicker
                autoOk
                disableToolbar
                variant="inline"
                format={getInputDateFormats(i18n.language)}
                id="createdIn"
                fullWidth
                label={t("forms.endDateLabel")}
                value={meetEndDate}
                onChange={(date) =>
                  setMeetEndDate(date ? startOfDay(date) : null)
                }
                KeyboardButtonProps={{
                  "aria-label": "change date",
                }}
              />
            </Grid>

            <Grid item lg={12} xs={12}>
              <Textarea
                name="freeField"
                id="freeField"
                onChange={(e) => setOpenedField(e.target.value)}
                value={openedField}
                style={{ width: "100%" }}
                label={t("forms.openedField")}
              />

              <OpenedInputDescription>
                {t("forms.filesFreeInput")}
              </OpenedInputDescription>
            </Grid>
          </Grid>
        </FieldsContainer>

        <ButtonsContainer>
          <Button
            variant="contained"
            onClick={handleCleanFilters}
            className={classes.buttonOutlined}
          >
            {t("forms.cleanFilters")}
          </Button>

          <Button
            variant="contained"
            onClick={handleApplyFilters}
            className={classes.button}
          >
            {t("forms.applyFilters")}
          </Button>
        </ButtonsContainer>
      </Content>
    </Box>
  );
}

export default FilterDrawer;
